import { DateTime } from "luxon";
import { useTranslate } from "@tolgee/react";
import { PriorityIcon } from "@frontend/lyng/common/Priority";
import {
  ActivitySchedule,
  ActivityStatus,
  Priority,
  SubjectFieldsFragment,
} from "../../../api/generated/graphql";
import { formatTime } from "../../../utils/dateUtils";
import { Headline, Label, Link, Paragraph } from "@frontend/lyng/typography";
import { useCareContext } from "../../../providers";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { match } from "ts-pattern";
import { useCallback, useMemo, useState } from "react";
import { Button } from "@frontend/lyng/button";
import { Reporter } from "../../../types";
import { Checkmark } from "@frontend/lyng/assets/icons/16/outline";

type Props = {
  priority: Priority;
  title: string;
  isDue: boolean;
  schedule: ActivitySchedule | null;
  lastCompletedAt: string | null;
  description: string;
  start?: DateTime;
  subject?: SubjectFieldsFragment;
  status: ActivityStatus;
  completedBy?: Reporter | null;
  comment?: string | null;
};

const getIconAndText = (priority: Priority) =>
  match(priority)
    .with(Priority.High, () => ({
      icon: <PriorityIcon className="mr-1" priority={Priority.High} />,
      text: "priorities.high",
    }))
    .with(Priority.Medium, () => ({
      icon: <PriorityIcon className="mr-1" priority={Priority.Medium} />,
      text: "priorities.medium",
    }))
    .with(Priority.Low, () => ({
      icon: <PriorityIcon className="mr-1" priority={Priority.Low} />,
      text: "priorities.low",
    }))
    .otherwise(() => ({ icon: null, text: null }));

function calculateDaysUntilNextDue(
  intervalDays: number,
  lastCompletedAt: string | null,
): number {
  const today = DateTime.local().startOf("day");

  const lastCompletedDate = lastCompletedAt
    ? DateTime.fromISO(lastCompletedAt).startOf("day")
    : null;

  if (!lastCompletedDate) {
    return 0;
  }

  const daysSinceLastCompleted = today.diff(lastCompletedDate, "days").days;

  if (daysSinceLastCompleted === 0) {
    return intervalDays;
  } else if (daysSinceLastCompleted % intervalDays === 0) {
    return 0;
  } else {
    const daysSinceLastCompletionModulo = daysSinceLastCompleted % intervalDays;
    return intervalDays - daysSinceLastCompletionModulo;
  }
}

export const ShiftActivityCard = ({
  priority,
  isDue,
  schedule,
  lastCompletedAt,
  title,
  description,
  start,
  subject,
  status,
  completedBy,
  comment,
}: Props) => {
  const { t } = useTranslate();
  const {
    state: { viewer },
  } = useCareContext();
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  const [isExpanded, setIsExpanded] = useState(false);

  const getDueInText = useMemo(
    () =>
      (daysUntilNextDue: number): string => {
        if (daysUntilNextDue === 0) {
          return t("activities.dueToday");
        } else if (daysUntilNextDue === 1) {
          return t("activities.dueInOneDay");
        } else {
          return t("activities.dueInXDays", { daysUntilNextDue });
        }
      },
    [t],
  );

  const getScheduleDescription = useCallback(
    (schedule: ActivitySchedule, lastCompletedAt: string | null): string => {
      if (schedule.__typename === "ActivityScheduleEveryNDays") {
        const intervalDays = schedule.days;

        const daysUntilNextDue = calculateDaysUntilNextDue(
          intervalDays,
          lastCompletedAt,
        );

        const dueInText = getDueInText(daysUntilNextDue);

        return t("activities.repeatsEvery", { intervalDays, dueInText });
      } else if (schedule.__typename === "ActivityScheduleEveryVisit") {
        return t("activities.repeatsEveryVisit");
      } else if (schedule.__typename === "ActivityScheduleFollowVisitSeries") {
        return t("activities.followVisitSeries");
      }

      return "";
    },
    [getDueInText, t],
  );

  const scheduleDescription = schedule
    ? getScheduleDescription(schedule, lastCompletedAt)
    : "";

  const { icon, text } = useMemo(() => getIconAndText(priority), [priority]);

  const assignedFor = useMemo(() => {
    return match(subject)
      .with({ __typename: "CareRecipient" }, (sub) => nameOrderFn(sub))
      .with({ __typename: "CareHome" }, () => null)
      .otherwise(() => null);
  }, [subject, nameOrderFn]);

  const activityStatusRow = useMemo(() => {
    if (status === ActivityStatus.NotStarted) {
      return null;
    }

    if (lastCompletedAt && completedBy && completedBy.id !== viewer?.id) {
      return (
        <ShiftActivityStatusRow
          status={status}
          completedBy={completedBy}
          lastCompletedAt={lastCompletedAt}
          comment={comment}
        />
      );
    }

    return null;
  }, [status, lastCompletedAt, completedBy, comment, viewer?.id]);

  return (
    <div className="text-title py-2">
      <div className="px-2">
        <Headline size="m">{title}</Headline>
        {assignedFor && (
          <div className="flex items-center gap-2">
            {assignedFor && (
              <Link size="xs" to={`./carerecipient/${subject?.id}`}>
                {t("activities.assignedFor", { assignedFor })}
              </Link>
            )}
          </div>
        )}
      </div>
      <div className="py-4 px-2 relative">
        <div
          className={`overflow-hidden transition-all duration-200 ${isExpanded ? "max-h-none" : "max-h-[4.5rem]"}`}
        >
          <Paragraph size="s">
            {isExpanded
              ? description
              : description.length > 150
                ? `${description.slice(0, 150)}...`
                : description}
          </Paragraph>
        </div>
        {description.length > 150 && !isExpanded && (
          <div className="absolute -bottom-0 right-0">
            <Button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-primary-600 text-sm font-medium bg-greyscale-900 px-1"
              text={t("common.seeMore")}
              variant="tertiary"
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 px-2">
        {icon}
        {activityStatusRow ? (
          <div className="px-2 mt-2">{activityStatusRow}</div>
        ) : (
          <>
            {!isDue && (
              <Label size="xxs" className="text-subtitle">
                {scheduleDescription}
              </Label>
            )}
            {start && isDue && (
              <Label size="xxs" className="text-subtitle">
                {formatTime(start, false)}
              </Label>
            )}
            {text && isDue && (
              <Label size="xxs" className="text-subtitle">
                {t(text)}
                {schedule ? ` - ${scheduleDescription}` : ""}
              </Label>
            )}
          </>
        )}
      </div>
    </div>
  );
};

type ShiftActivityStatusRowProps = {
  status: ActivityStatus;
  completedBy: Reporter;
  lastCompletedAt?: string | null;
  comment?: string | null;
};

export const ShiftActivityStatusRow = ({
  status,
  completedBy,
  lastCompletedAt,
  comment,
}: ShiftActivityStatusRowProps) => {
  const { t } = useTranslate();
  const {
    state: { viewer },
  } = useCareContext();
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);

  if (!lastCompletedAt || !completedBy) {
    return null;
  }

  const isCompleted = status === ActivityStatus.Completed;

  return (
    <Paragraph
      size="xs"
      type={isCompleted ? "tertiary" : "critical"}
      className={`mb-${isCompleted ? "1" : "2"} flex items-center gap-1`}
    >
      {t("shiftDetails.activities.completedBy", {
        completedBy: nameOrderFn(completedBy),
        time: formatTime(DateTime.fromISO(lastCompletedAt), false),
      })}
      {isCompleted && <Checkmark className="w-4 h-4 text-success-600" />}
      {comment && `: "${comment}"`}
    </Paragraph>
  );
};
